import React, { forwardRef, useCallback, useEffect, useState } from "react";
import RadioButton from "./RadioButton";
import { createUniqueKey, getClassNames, transformOptions } from "../../utils";

import "./RadioButton.css";
import withTheme from "../../hocs/withTheme";
import FormGroup from "../FormGroup/FormGroup";
import FormControlMessage from "../FormControlMessage/FormControlMessage";
import { getDataOrAriaProps } from "../../../utils";

type RadioButtonOption = {
    label: string;
    value: string;
    disabled?: boolean;
    description?: string;
};

type RadioButtonGroupProps = React.HTMLAttributes<HTMLDivElement> & {
    defaultValue?: string | null;
    disabled?: boolean;
    name?: string | null;
    options?: RadioButtonOption[];
    horizontal?: boolean;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    children?: React.ReactNode;
    className?: string | null;
    value?: string | null;
    error?: string | null;
    innerRef?: React.Ref<HTMLInputElement>;
};

export const RadioButtonGroup = (props: RadioButtonGroupProps) => {
    const {
        defaultValue = null,
        disabled = false,
        name = null,
        options = [],
        horizontal = false,
        onChange = () => {},
        children = null,
        className = null,
        value = null,
        error = null,
        innerRef: ref = null,
        ...groupProps
    } = props;

    const [checked, setChecked] = useState(value || defaultValue);

    useEffect(() => {
        setChecked(value);
    }, [value]);

    const onChangeValue = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setChecked(e.target.value);
            onChange(e);
        },
        [onChange, setChecked]
    );

    let childrenToRender = children;

    if (options) {
        childrenToRender = transformOptions(options).map(
            (radioButtonProps, index) => (
                <RadioButton
                    ref={ref}
                    {...radioButtonProps}
                    key={createUniqueKey(index, radioButtonProps.label)}
                    id={`${radioButtonProps.value}:${Math.ceil(
                        Math.random() * 1000000
                    )}`}
                    error={error}
                    checked={checked === radioButtonProps.value}
                    onChange={onChangeValue}
                    disabled={radioButtonProps?.disabled}
                />
            )
        );
    }

    return (
        <FormGroup disabled={disabled} error={error ?? undefined}>
            <div
                {...groupProps}
                {...getDataOrAriaProps(props as unknown as Record<string, unknown>)}
                className={getClassNames(
                    className,
                    "radio-group",
                    "radio-group-outline",
                    { "radio-group-horizontal": horizontal }
                )}
            >
                {childrenToRender}
            </div>
            {error && <FormControlMessage>{error}</FormControlMessage>}
        </FormGroup>
    );
};


export default forwardRef<HTMLInputElement, RadioButtonGroupProps>((props, ref) =>
    withTheme(RadioButtonGroup)({
        ...props,
        innerRef: ref,
        className: props.className ?? undefined,
    })
);
